import { Container, Graphics, Sprite, Text } from "pixi.js";
import { type Card } from "../model/types.ts";
import { extractTitle } from "./markdown.ts";
import { type TextureCache } from "./textureCache.ts";
import { tierFor } from "./lod.ts";
import { isImagePath, isPdfPath } from "../vault/attachments.ts";
import { inkColor, paintStroke } from "./ink.ts";
import { TEXT_RADIUS, hasTextBackground } from "./textStyle.ts";
import { UI_SELECTION, UI_PAPER } from "./uiColors.ts";

/**
 * Direcciones de resize. Ahora las ocho valen para TODO lo que se puede
 * redimensionar —tarjetas, formas y contenedores—: los cuatro lados existían
 * sólo para el contenedor, y estirar una tarjeta a lo ancho sin tocarle el alto
 * obligaba a arrastrar una esquina y corregir el otro eje a ojo.
 */
export type ResizeDir = "nw" | "ne" | "sw" | "se" | "n" | "s" | "e" | "w";

/** Medidas del cromo de selección, en px de PANTALLA (se dividen por el zoom). */
const ELBOW_LEN_PX = 6; // largo de cada brazo del codito de esquina
const ELBOW_THICK_PX = 3.5; // grosor del codito (con su borde queda como la barrita)
const BAR_LEN_PX = 20; // largo de la barrita de lado
const BAR_THICK_PX = 5; // grosor de la barrita de lado
/**
 * Brazo mínimo del codito, en px de PANTALLA. Con la caja muy chica los brazos
 * se recortan para no cruzarse entre esquinas, pero por debajo de esto dejan de
 * leerse como una esquina y se vuelven un punto.
 */
const ELBOW_MIN_LEN_PX = 4;
/**
 * Por debajo de este largo en pantalla, el lado no dibuja su barrita: en una
 * caja chica o muy alejada las cuatro barritas se tocan entre sí y con las
 * esquinas, y el cromo tapa lo que está seleccionado. El lado se sigue pudiendo
 * agarrar igual — la zona de click no depende de que haya algo dibujado.
 */
const BAR_MIN_SIDE_PX = 44;

export interface SelectionChrome {
  /**
   * Dibujar los agarres. Con varias cosas seleccionadas se muestra sólo el
   * contorno: ocho agarres por tarjeta serían ruido, y el gesto de resize no
   * está disponible para una selección múltiple.
   */
  handles?: boolean;
  /** Agarre bajo el puntero, que se pinta lleno para confirmar qué se va a tomar. */
  hover?: ResizeDir | null;
  /**
   * Dibujar el recuadro azul. El contenedor lo apaga: ya tiene el suyo, del
   * color del grupo, y encimarle uno azul le borraría esa identidad.
   */
  outline?: boolean;
}

/**
 * El recuadro de selección y sus agarres, en coordenadas locales del nodo. Vive
 * suelto y no adentro de `CardView` porque `ShapeView` y `GroupView` dibujan
 * exactamente el mismo: seleccionar tiene que verse igual sin importar qué se
 * seleccionó, y tres copias de esto se separarían a la primera de cambio.
 *
 * El recuadro es SIEMPRE rectangular, aun sobre un rombo o una elipse: es lo
 * que hace Figma, y marca el rect que se va a redimensionar y no la silueta.
 *
 * Los agarres son ocho: cuatro coditos redondeados en las esquinas (resize
 * libre) y cuatro barritas en el medio de cada lado (resize de un solo eje). Las
 * barritas son además la parte visual del arreglo que pide no tener que cazar
 * el punto exacto: marcan dónde está la banda agarrable de cada lado, que es
 * bastante más ancha que ellas.
 */
export function paintSelection(
  g: Graphics,
  width: number,
  height: number,
  zoom: number,
  chrome: SelectionChrome = {},
): void {
  const s = 1 / zoom; // screen-px → world-px
  const stroke = 1.5 * s;

  g.clear();
  if (chrome.outline !== false) {
    g.rect(0, 0, width, height).stroke({ width: stroke, color: UI_SELECTION, alignment: 0.5 });
  }
  if (!chrome.handles) return;

  const hover = chrome.hover ?? null;
  const paint = (dir: ResizeDir): void => {
    g.fill(dir === hover ? UI_SELECTION : UI_PAPER).stroke({
      width: stroke,
      color: UI_SELECTION,
      alignment: 0.5,
    });
  };

  // Barritas de lado primero, para que un codito de esquina quede por
  // encima cuando la caja es tan chica que se rozan.
  const barLen = BAR_LEN_PX * s;
  const barThick = BAR_THICK_PX * s;
  const radius = barThick / 2;
  if (width * zoom >= BAR_MIN_SIDE_PX) {
    const x = (width - barLen) / 2;
    g.roundRect(x, -barThick / 2, barLen, barThick, radius);
    paint("n");
    g.roundRect(x, height - barThick / 2, barLen, barThick, radius);
    paint("s");
  }
  if (height * zoom >= BAR_MIN_SIDE_PX) {
    const y = (height - barLen) / 2;
    g.roundRect(-barThick / 2, y, barThick, barLen, radius);
    paint("w");
    g.roundRect(width - barThick / 2, y, barThick, barLen, radius);
    paint("e");
  }

  // Coditos de esquina: dos brazos sobre los bordes que se juntan en la punta,
  // con las puntas y el codo redondeados. Reemplazan al cuadradito, que sobre
  // una tarjeta con esquinas redondeadas quedaba como un pegote suelto: el
  // codito sigue la forma del recuadro y además dice hacia dónde estira.
  //
  // En una caja chica los brazos se recortan a un tercio del lado para que dos
  // esquinas vecinas no se toquen; por debajo del mínimo la esquina no se
  // dibuja (se sigue pudiendo agarrar: la zona de click no depende del dibujo).
  const elbowThick = ELBOW_THICK_PX * s;
  const armLen =
    Math.min(ELBOW_LEN_PX, (width * zoom) / 3, (height * zoom) / 3) * s;
  if (armLen < ELBOW_MIN_LEN_PX * s) return;

  const corners: Array<[ResizeDir, number, number, number, number]> = [
    ["nw", 0, 0, 1, 1],
    ["ne", width, 0, -1, 1],
    ["sw", 0, height, 1, -1],
    ["se", width, height, -1, -1],
  ];
  for (const [dir, cx, cy, sx, sy] of corners) {
    // Dos pasadas sobre el mismo trazo: la de abajo, más gruesa y azul, hace de
    // borde; la de arriba lo rellena de papel (o de azul cuando está bajo el
    // puntero, igual que las barritas).
    const trace = (): void => {
      g.moveTo(cx, cy + sy * armLen)
        .lineTo(cx, cy)
        .lineTo(cx + sx * armLen, cy);
    };
    trace();
    g.stroke({
      width: elbowThick + 2 * stroke,
      color: UI_SELECTION,
      cap: "round",
      join: "round",
    });
    trace();
    g.stroke({
      width: elbowThick,
      color: dir === hover ? UI_SELECTION : UI_PAPER,
      cap: "round",
      join: "round",
    });
  }
}

/**
 * Cuánto se extiende la zona agarrable de cada lado, en px de PANTALLA: hacia
 * afuera del borde y hacia adentro. Que sobresalga hacia afuera es medio arreglo
 * de "no tener que cazar el punto exacto" —el borde se agarra viniendo de
 * cualquiera de los dos lados— y hacia adentro se entra poco para no comerle el
 * área de arrastre a una tarjeta.
 */
export interface HitBand {
  outset: number;
  inset: number;
  /** Lado del cuadrado de esquina, medido desde la esquina hacia adentro. */
  corner: number;
}

export const CARD_HIT_BAND: HitBand = { outset: 7, inset: 7, corner: 14 };

/**
 * Qué zona de resize (si alguna) hay bajo un punto de MUNDO, para un rect y una
 * banda dados. Devuelve la dirección para que quien redimensiona pueda anclar el
 * borde o la esquina opuesta.
 *
 * Una sola implementación para tarjetas, formas y contenedores: antes eran dos
 * —esquinas por un lado, lados por el otro— y por eso sólo el contenedor tenía
 * los ocho. Lo único que cambia entre ellos es el ancho de la banda.
 */
export function resizeDirAt(
  rect: { x: number; y: number; width: number; height: number },
  worldX: number,
  worldY: number,
  zoom: number,
  band: HitBand = CARD_HIT_BAND,
): ResizeDir | null {
  const outset = band.outset / zoom;
  // En una caja chica la banda se comería el interior entero y no quedaría por
  // dónde arrastrarla; las esquinas, peor, se comerían los lados. Las dos se
  // recortan a una fracción del lado para que siempre sobre algo en el medio.
  const inset = Math.min(band.inset / zoom, rect.width / 4, rect.height / 4);
  const corner = Math.min(band.corner / zoom, rect.width / 3, rect.height / 3);

  // Distancia a cada lado; negativa = el punto quedó del lado de afuera.
  const dw = worldX - rect.x;
  const de = rect.x + rect.width - worldX;
  const dn = worldY - rect.y;
  const ds = rect.y + rect.height - worldY;
  if (dw < -outset || de < -outset || dn < -outset || ds < -outset) return null;

  const nearW = dw <= inset;
  const nearE = de <= inset;
  const nearN = dn <= inset;
  const nearS = ds <= inset;
  // Bien adentro de la caja no hay resize: ahí el gesto es mover.
  if (!nearW && !nearE && !nearN && !nearS) return null;

  const cw = dw <= corner;
  const ce = de <= corner;
  const cn = dn <= corner;
  const cs = ds <= corner;
  if (cn && cw) return "nw";
  if (cn && ce) return "ne";
  if (cs && cw) return "sw";
  if (cs && ce) return "se";

  if (nearN) return "n";
  if (nearS) return "s";
  if (nearW) return "w";
  if (nearE) return "e";
  return null;
}

/**
 * One card in the GPU world. Holds a background rect, a title label (LOD-far),
 * and a sprite (LOD-near, backed by the cached texture). Both text and `file`
 * nodes render full-bleed — `file` nodes no longer show a filename header.
 */
export class CardView {
  readonly container = new Container();
  private bg = new Graphics();
  private title = new Text({
    text: "",
    style: {
      fill: 0x1a1a1f,
      fontSize: 18,
      fontFamily: "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
      fontWeight: "600",
      wordWrap: true,
      wordWrapWidth: 200,
    },
  });
  private sprite = new Sprite();
  private ink = new Graphics();
  /** El cromo de selección. CanvasView lo dibuja en su propia capa (ver
   *  `chromeLayer`) para que una tarjeta de encima no lo tape. */
  readonly selection = new Graphics();

  private lastTitleSrc = "";
  /** Firma del fondo de un nodo de texto (rect + color), para no repintar un
   *  vector que no cambió. El resto de las tarjetas usa lastW/lastH. */
  private lastBgKey = "";
  private lastW = -1;
  private lastH = -1;
  selected = false;
  private chrome: SelectionChrome = {};

  constructor(public card: Card) {
    this.container.eventMode = "none"; // hit-testing handled centrally by CanvasView
    this.sprite.visible = false;
    this.container.addChild(this.bg, this.sprite, this.title, this.ink, this.selection);
    this.title.position.set(16, 14);
  }

  /** Re-read the card model and pick LOD / texture for the current zoom. */
  update(card: Card, zoom: number, dpr: number, cache: TextureCache): void {
    this.card = card;
    this.container.position.set(card.x, card.y);

    // Ink cards are pure vector — no bg, no texture pipeline, no fallback text.
    // The stroke only needs repainting when the card rect changes (points are
    // normalized to it); selection follows the same path as other cards.
    if (card.type === "ink") {
      if (card.width !== this.lastW || card.height !== this.lastH) {
        this.redrawInk(card);
        this.lastW = card.width;
        this.lastH = card.height;
      }
      if (this.selected) this.redrawSelection(card, zoom);
      else this.selection.clear();
      return;
    }

    // Un nodo de texto tampoco pasa por el pipeline de texturas: sus letras las
    // dibuja la capa DOM (`canvasText.ts`), y acá queda sólo su fondo —que
    // puede no existir— y el cromo de selección. Es lo que lo saca del ciclo de
    // rasterizar-por-tecla que tenía cuando era markdown.
    if (card.type === "text") {
      const bgKey = `${card.width}|${card.height}|${card.text?.background ?? ""}`;
      if (bgKey !== this.lastBgKey) {
        this.lastBgKey = bgKey;
        this.redrawBg(card);
      }
      this.sprite.visible = false;
      this.title.visible = false;
      if (this.selected) this.redrawSelection(card, zoom);
      else this.selection.clear();
      return;
    }

    // Un PDF muestra su portada: es un bitmap, igual que una imagen, así que
    // tampoco necesita el texto de reemplazo mientras se rasteriza.
    const isImage =
      card.type === "file" && (isImagePath(card.file) || isPdfPath(card.file));

    if (card.width !== this.lastW || card.height !== this.lastH) {
      this.redrawBg(card);
      this.lastW = card.width;
      this.lastH = card.height;
    }
    this.configureFallbackText(card);

    if (card.markdown !== this.lastTitleSrc) {
      this.title.text = extractTitle(card.markdown);
      this.lastTitleSrc = card.markdown;
    }

    // Always render the full markdown texture so the body text never vanishes,
    // at any zoom. The texture is cached and rasterized at most once per
    // content/tier, so panning/zooming still never re-rasterizes (golden rule).
    const tier = tierFor(zoom, dpr);
    // Una nota embebida cuyo cuerpo todavía se está leyendo tiene el markdown
    // vacío: rasterizarla así gastaba un `html-to-image` entero (su costo es
    // casi fijo por tarjeta) en una textura que se tira apenas llega el texto,
    // y ocupaba uno de los pocos turnos de rasterizado mientras las demás
    // esperaban.
    const loading = card.type === "file" && !isImage && !card.markdown;
    if (!loading) cache.request(card, tier);
    const tex = cache.get(card, tier) ?? cache.getAny(card.id);
    if (tex) {
      this.sprite.texture = tex;
      this.sprite.position.set(0, 0);
      this.sprite.width = card.width;
      this.sprite.height = card.height;
      this.sprite.visible = true;
      this.title.visible = false;
    } else {
      // Texture not ready yet — show a cheap fallback meanwhile.
      this.sprite.visible = false;
      this.title.visible = !isImage;
    }

    if (this.selected) this.redrawSelection(card, zoom);
    else this.selection.clear();
  }

  private configureFallbackText(card: Card): void {
    this.title.position.set(16, 14);
    this.title.style.fill = 0x1a1a1f;
    this.title.style.fontFamily = "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif";
    this.title.style.fontSize = 18;
    this.title.style.fontWeight = "600";
    this.title.style.lineHeight = 0;
    this.title.style.wordWrapWidth = card.width - 32;
  }

  /** Repaint the stroke scaled from its normalized points to the card rect. */
  private redrawInk(card: Card): void {
    this.ink.clear();
    const ink = card.ink;
    if (!ink) return;
    const pts = ink.points;
    const abs = new Array<number>(pts.length);
    for (let i = 0; i < pts.length; i += 2) {
      abs[i] = pts[i] * card.width;
      abs[i + 1] = pts[i + 1] * card.height;
    }
    // Scale the stroke width with the card, averaging both axes so a
    // non-uniform resize still looks reasonable.
    const scale = (card.width / ink.naturalWidth + card.height / ink.naturalHeight) / 2;
    paintStroke(this.ink, abs, inkColor(ink.color), ink.size * scale);
  }

  private redrawBg(card: Card): void {
    this.bg.clear();
    // Un nodo de texto pinta lo que se haya elegido: nada (el caso normal: son
    // letras sueltas sobre el lienzo) o una pastilla sólida detrás. Sin borde:
    // el fondo está para despegar el texto de lo que tenga debajo, no para
    // convertirlo en una tarjeta.
    if (card.type === "text") {
      if (!hasTextBackground(card.text)) return;
      this.bg
        .roundRect(0, 0, card.width, card.height, TEXT_RADIUS)
        .fill(inkColor(card.text!.background));
      return;
    }
    this.bg
      // Mismo radio que los grupos (GROUP_RADIUS = 4 en GroupView) para que
      // el borde siga la misma curva que la textura del markdown.
      .roundRect(0, 0, card.width, card.height, 4)
      .fill(0xf5f5f5)
      .stroke({ width: 1, color: 0xe0e0e0, alignment: 1 });
  }

  private redrawSelection(card: Card, zoom: number): void {
    paintSelection(this.selection, card.width, card.height, zoom, this.chrome);
  }

  setSelected(v: boolean, chrome: SelectionChrome = {}): void {
    this.selected = v;
    this.chrome = chrome;
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
